package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.AtRiskItemDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Text

@Composable
fun AtRiskPanel(
    items: List<AtRiskItemDto>,
    incompleteMessage: String?,
    zone: TimeZone,
) {
    if (items.isEmpty() && incompleteMessage == null) return
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.sm)) {
            SpanText(
                text = AtRiskRules.title(items).takeIf { items.isNotEmpty() } ?: "At risk",
                modifier = Modifier.fontSize(Typography.bodySmall.size).fontWeight(600).color(Palette.textPrimary),
            )
            incompleteMessage?.let {
                SpanText(
                    text = it,
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.warning),
                )
            }
            items.forEach { AtRiskRow(it, zone) }
        }
    }
}

@Composable
private fun AtRiskRow(
    item: AtRiskItemDto,
    zone: TimeZone,
) {
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.xxs)) {
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Badge(label = AtRiskRules.reasonLabel(item.reason), tone = toneOf(item))
            SpanText(
                text = item.ownerName ?: "Someone",
                modifier = Modifier.fontSize(Typography.bodySmall.size).fontWeight(600).color(Palette.textPrimary),
            )
            A(
                href = item.url,
                attrs = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.accentText)
                    .toAttrs {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    },
            ) { Text(item.title) }
        }
        SpanText(
            text = listOfNotNull(AtRiskRules.idleLabel(item), AtRiskRules.reviewersLabel(item)).joinToString(" · "),
            modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textSecondary),
        )
        AtRiskRules.flaggedLabel(item, zone)?.let {
            SpanText(text = it, modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary))
        }
    }
}

private fun toneOf(item: AtRiskItemDto): BadgeTone =
    when {
        !item.listedInPost -> BadgeTone.Neutral
        item.reason == AtRiskRules.BLOCKED -> BadgeTone.Error
        else -> BadgeTone.Warning
    }
